import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Container, IconButton } from '@mui/material';
import { leadHandler } from '../../handlers/leadHandlers';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MaterialReactTable, getMRT_RowSelectionHandler, useMaterialReactTable } from 'material-react-table';
import LeadStore from '../../store/LeadStore';
import { datetimeFormatter } from '../../utils/datetimeFormatter';
import dayjs from 'dayjs';
import AuthStore from '../../store/AuthStore';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enIN } from 'date-fns/locale';
import { isEmpty } from '../../utils/helper';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useLocation, useParams } from 'react-router-dom';

export const LeadersListReactQuery = observer(({ handleSelectedRowChange, toggleLeadDetails }) => {
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const pId = queryParams.get('pId');

  const columnFunction = () => [
    { accessorKey: 'id', header: 'Id', size: 50, enableColumnFilter: false },
    {
      accessorKey: 'copy',
      id: 'copyButton',
      enableGlobalFilter: false,
      enableKeyboardShortcuts: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      enableSorting: false,
      header: '',
      size: 50,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <IconButton aria-label="copy" size="small" onClick={(e) => handleCopyRow(e, row)}>
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      ),
    },
    {
      accessorKey: 'customerName',
      header: 'Name',
      size: 50,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'contactNo',
      header: 'Contact Number',
      size: 50,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'siteDimension',
      header: 'Dimension',
      size: 50,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'builtUpArea',
      header: 'Built up area',
      size: 50,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'projectValue',
      header: 'Project Value',
      size: 50,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'signupDate',
      header: 'Signup Date',
      size: 50,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'workFlowStatusName',
      header: 'Present Status',
      enableColumnFilter: true,
      size: 50,
      filterVariant: 'multi-select',
      filterSelectOptions: LeadStore.workflowStatusByPipeLine.map((item) => item.name),
    },
    {
      accessorKey: 'currentWorkflowId',
      header: 'Workflow Status Id',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'workFlowSubStatusName',
      header: 'Workflow Sub Status Name',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'leadPriority',
      header: 'Priority',
      size: 50,
      enableColumnFilter: true,
      filterVariant: 'multi-select',
      filterSelectOptions: LeadStore.priorities.map((item) => item.name),
    },
    {
      accessorKey: 'siteLocation',
      header: 'Site Location',
      size: 50,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'leadHandledBy',
      header: 'Lead Owner',
      size: 50,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'floorPlanFinalisedDate',
      header: 'Floor Plan Finalised Date',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'contractClosingDate',
      header: 'Contract Closing Date',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'commencementDate',
      header: 'Commencement Date',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'elevationFinalisedDate',
      header: 'Elevation Finalised Date',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'masterFileFinalisedDate',
      header: 'Master File Finalised Date',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'projectManager',
      header: 'Project Manager',
      size: 50,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'siteEngineer',
      header: 'Site Engineer',
      size: 50,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'formattedCallReminderDate',
      header: 'Call Reminder',
      size: 50,
      enableColumnFilter: false,
    },

    // Hidden columns by default, but enabled for Show/Hide column functionality
    {
      accessorKey: 'leadSource',
      header: 'Lead Source',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'leadDate',
      header: 'Lead Date',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'soilTestDate',
      header: 'Soil Test Date',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'pipeLinesId',
      header: 'Pipeline ID',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'requirement',
      header: 'Requirement',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'currentWorkflowName',
      header: 'Current Workflow',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'workFlowSubStatusName',
      header: 'Substatus Name',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'workFlowSubStatusId',
      header: 'Substatus ID',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'leadCallName',
      header: 'Lead Call Name',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'callStatus',
      header: 'Call Status',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'message',
      header: 'Message',
      size: 50,
      enableColumnFilter: false,
      enableHiding: true,
      columnVisibility: false,
    },
    {
      accessorKey: 'callReminderDate',
      header: 'Call Reminder Date',
      size: 50,
      enableColumnFilter: false,
    },
  ];

  const [columns, setColumns] = useState(columnFunction);

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState([{ id: 'customerName', desc: false }]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 30,
  });
  const response = useQuery({
    queryKey: ['leadersList', columnFilters, globalFilter, pagination, sorting, pId],
    queryFn: async () => {
      const sortingOrder = sorting?.[0]?.desc ? 'desc' : 'asc';
      const sortColumn = sorting?.[0]?.id;
      LeadStore.setCanPerformMassStatusUpdate(
        columnFilters.find((item) => item.id === 'workFlowStatusName')?.value.length === 1
      );
      const res = await leadHandler.getLeadersList(
        columnFilters,
        globalFilter ?? '',
        sortColumn,
        sortingOrder,
        pagination.pageIndex,
        pagination.pageSize,
        AuthStore.userDetails.Id,
        pId
      );
      return res.data;
    },
    placeholderData: keepPreviousData,
  });

  const handleCopyRow = (e, row) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent default action
    const rowData = columns
      .map((col) => {
        const value = row.original[col.accessorKey];
        return value !== null && value !== undefined && value !== '' ? `${col.header}: ${value}` : null;
      })
      .filter(Boolean)
      .join(', ');
    navigator.clipboard
      .writeText(rowData)
      .then(() => {
        console.log('Row data copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy row data: ', err);
      });
  };

  const formatData = (items) => {
    items.forEach((item) => {
      item.formattedCallReminderDate = checkDateTimeIsValid(item.callReminderDate)
        ? datetimeFormatter.formatDateTime(new Date(item.callReminderDate), datetimeFormatter.toLocal)
        : '';
    });

    return items;
  };

  const checkDateTimeIsValid = (callReminderDate) => {
    if (isEmpty(callReminderDate) || callReminderDate === 'Invalid Date') {
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    setColumns(columnFunction);
  }, [pId, LeadStore.workflowStatusByPipeLine]);

  useEffect(() => {
    setColumnFilters([]);
  }, [pId]);

  const table = useMaterialReactTable({
    columns,
    data: response?.data?.items ? formatData(response?.data?.items) : [],
    initialState: {
      showColumnFilters: true,
      columnVisibility: {
        id: false,
        pipeLinesId: false,
        currentWorkflowId: false,
        leadPriority: false,
        callReminderDate: false,
      },
      density: 'compact', // values can be 'spacious', 'comfortable', 'compact'
    },
    headerStyle: { backgroundColor: '#cce6e6' },
    // enableColumnOrdering: false,
    // enableColumnResizing: false,
    enableDensityToggle: false,
    enableColumnFilters: true,
    columnFilterDisplayMode: 'popover',
    enableColumnFilterModes: false,
    // enableColumnActions: false,
    enableStickyHeader: true,
    // enableHiding: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    // positionPagination: 'top',
    muiPaginationProps: {
      rowsPerPageOptions: [30, 50, 100],
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: `${window.innerHeight - 257}px`,
        height: `${window.innerHeight - 257}px`,
      },
    },
    muiToolbarAlertBannerProps: response.isError
      ? {
          color: 'error',
          children: 'Error loading data',
        }
      : undefined,
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: '#cce6e6',
        '& .MuiTableSortLabel-root svg': {
          color: '#F07B0E !important',
        },
        '& .MuiCheckbox-root svg': {
          color: '#F07B0E !important',
        },
        '& .MuiIconButton-root svg': {
          color: '#F07B0E !important',
        },
      },
    },
    muiTablePaperProps: ({ table }) => ({
      //not sx
      style: {
        margin: table.getState().isFullScreen ? '95px 0px 0px 0px' : '0px',
      },
    }),
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    paginationDisplayMode: 'pages',
    getRowId: (row) => row.id,
    enableRowSelection: true,
    autoResetSelectedRows: true,
    //clicking anywhere on the row will select it
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        toggleLeadDetails(row.original);
      },
      selected: rowSelection[row.id],
      sx: {
        cursor: 'pointer',
      },
    }),
    onRowSelectionChange: setRowSelection,
    rowCount: response?.data?.totalCount ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading: response.isLoading,
      pagination,
      showAlertBanner: response.isError,
      showProgressBars: response.isFetching,
      sorting,
      rowSelection,
    },
  });

  useEffect(() => {
    setRowSelection([]);
    response?.refetch();
  }, [LeadStore.refreshLeadersBoard]);

  useEffect(() => {
    LeadStore.setLeadsList(response?.data?.items, columns);
  }, [response]);

  useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      handleSelectedRowChange([]);
    } else {
      handleSelectedRowChange(Object.keys(rowSelection));
    }
  }, [rowSelection]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
      <MaterialReactTable table={table} />
    </LocalizationProvider>
  );
});
